import styled from '@emotion/styled';
import { ThunkDispatch } from '@reduxjs/toolkit';

import { GameDetailsScore } from 'modules/games/domain/interfaces/game-details';

import { editGameScore, saveGameDetails } from 'modules/games/store/actions';
import { GameDetailsSliceState } from 'modules/games/store/game-details';
import { gameDetailsIsSavingSelector, gameEventsChangedSelector } from 'modules/games/store/selectors';
import { updateGameDetails } from 'modules/championship/store/stages/stages.slice';
import { useCallback, useMemo, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';
import { useGameDetails } from 'modules/games/ui/hooks/use-game-details';
import { useGameDetailsFetch } from 'modules/games/ui/hooks/use-game-details-fetch';
import { GameEventCreator } from '../game-event-creator';
import { GameResultEventsList } from '../game-result-events';
import { GameResultHeader } from '../game-result-header';
import { GameScoreForm } from '../game-score-form/game-score-form';
import MainStreamURL from './components/MainStreamURL';
import * as styles from './game-result.styles';

const ActionsPanel = styled.div`
  margin-top: 10px;
  padding: 20px 30px;
  display: flex;
  justify-content: end;
`;

type GameResultProps = {
  gameId: number;
  championshipId: number;
};

export const GameResult = ({ gameId, championshipId }: GameResultProps) => {
  const { t } = useLingui();
  const dispatch: ThunkDispatch<GameDetailsSliceState, {}, any> = useDispatch();

  useGameDetailsFetch(gameId, championshipId);

  const { isLoading, data } = useGameDetails();
  const eventsChanged = useSelector(gameEventsChangedSelector);
  const isDetailsSaving = useSelector(gameDetailsIsSavingSelector);

  const [isScoreFormOpen, setIsScoreFormOpen] = useState(false);
  const openScoreForm = useCallback(() => setIsScoreFormOpen(true), []);
  const closeScoreForm = useCallback(() => setIsScoreFormOpen(false), []);

  const isSaveButtonDisabled = useMemo(() => {
    return !eventsChanged || isDetailsSaving;
  }, [eventsChanged, isDetailsSaving]);

  const hasEvents = useMemo(() => data && data.events.length > 0, [data]);

  const showActionsPanel = useMemo(() => {
    return !isLoading && (hasEvents || eventsChanged);
  }, [isLoading, hasEvents, eventsChanged]);

  const handleChangeScoreClick = useCallback(
    (values: GameDetailsScore) => {
      dispatch(editGameScore(values));
      closeScoreForm();
    },
    [closeScoreForm, dispatch],
  );

  const handleSaveClick = useCallback(() => {
    if (!data || !eventsChanged || isDetailsSaving) {
      return;
    }

    dispatch(saveGameDetails(data))
      .unwrap()
      .then(() => {
        toast.success(t`Результаты матча успешно сохранены`);
        dispatch(updateGameDetails(data));
      })
      .catch(() => {
        toast.error(t`Ошибка сохранения результатов матча`);
      });
  }, [data, dispatch, eventsChanged, isDetailsSaving, t]);

  const listClassName = ['game-result-events', hasEvents ? '' : 'empty'].join(' ');

  return (
    <>
      <styles.GameResult>
        <GameResultHeader isLoading={isLoading} game={data} onEditScoreClick={openScoreForm} />

        <MainStreamURL game={data} isLoading={isLoading} />

        <div className="create-events">
          <GameEventCreator isLoading={isLoading} teamIndex={0} game={data} championshipId={championshipId} />
          <GameEventCreator isLoading={isLoading} teamIndex={1} game={data} championshipId={championshipId} />
        </div>
        <GameResultEventsList className={listClassName} isLoading={isLoading} game={data} championshipId={championshipId} />
        {showActionsPanel && (
          <ActionsPanel>
            <UDButton
              variant="primary"
              type="button"
              disabled={isSaveButtonDisabled}
              loading={isDetailsSaving}
              onClick={handleSaveClick}
            >
              {t`Сохранить и опубликовать`}
            </UDButton>
          </ActionsPanel>
        )}
      </styles.GameResult>

      <Modal show={isScoreFormOpen} onClose={closeScoreForm} size="md">
        <Modal.Header title={t`Изменить счет матча`} />
        <Modal.Body>
          {data && (
            <GameScoreForm
              teams={data!.teams}
              initialValues={{
                firstTeamScore: data!.result.firstTeamScore,
                secondTeamScore: data!.result.secondTeamScore,
              }}
              onSubmit={handleChangeScoreClick}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};
